import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { LogError, Metadata, RunView, UserInfo } from '@memberjunction/core';
import { UserService } from '../../../services/user.service';
import { NotificationService } from '../../../services/notification.service';
import { AnnouncementsEntity, AnnouncementTargetsEntity, AnnouncementUserStatesEntity } from 'mj_generatedentities';
import { DataGridColumn } from '../../../utils/interfaces';
import { AnnouncementManagementGridColumns } from '../../../utils/grid-constants';
import { Utils } from '../../../utils/utils';

interface AnnouncementWithStats {
  announcement: AnnouncementsEntity;
  totalTargets: number;
  readCount: number;
  unreadCount: number;
  acknowledgedCount: number;
  dismissedCount: number;
}

@Component({
  selector: 'acs-announcement-management',
  templateUrl: './acs-announcement-management.html',
  styleUrls: ['./acs-announcement-management.scss']
})
export class AcsAnnouncementManagementComponent implements OnInit {
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private utils = inject(Utils);

  announcements: AnnouncementWithStats[] = [];
  columns: DataGridColumn[] = [];
  loading = true;
  currentUser!: UserInfo;
  selectedStatus = 'all';
  searchText = '';
  showViewModal = false;
  showAnalyticsModal = false;
  selectedAnnouncement: AnnouncementsEntity | null = null;

  statusOptions = [
    { value: 'all', label: 'All Announcements' },
    { value: 'draft', label: 'Drafts' },
    { value: 'published', label: 'Published' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'expired', label: 'Expired' },
    { value: 'deleted', label: 'Deleted' }
  ];


  async ngOnInit(): Promise<void> {
    this.currentUser = this.userService.getUserInfo();
    this.setupDataGrid();
    await this.loadAnnouncements();
  }

  private setupDataGrid(): void {
    // Use columns from grid-constants and override handlers
    this.columns = AnnouncementManagementGridColumns.map(col => {
      if (col.key === 'actions' && col.actions) {
        return {
          ...col,
          actions: [
            {
              icon: '👁️',
              label: 'View',
              handler: (item: AnnouncementWithStats) => this.viewAnnouncement(item)
            },
            // {
            //   icon: '📊',
            //   label: 'Analytics',
            //   handler: (item: AnnouncementWithStats) => this.showAnnouncementAnalytics(item)
            // },
            {
              icon: '⏰',
              label: 'Expire',
              handler: (item: AnnouncementWithStats) => this.expireAnnouncement(item),
              disabled: (item: AnnouncementWithStats) => item.announcement.Status !== 'published'
            },
            {
              icon: '🗑️',
              label: 'Delete',
              handler: (item: AnnouncementWithStats) => this.deleteAnnouncement(item),
              disabled: (item: AnnouncementWithStats) => !this.canDelete(item.announcement)
            }
          ]
        };
      }
      // Override format functions to use component context
      if (col.key === 'Priority') {
        return {
          ...col,
          format: (value: string) => this.utils.getPriorityIcon(value)
        };
      }
      if (col.key === 'Status') {
        return {
          ...col,
          format: (value: string) => this.utils.getStatusBadge(value)
        };
      }
      if (col.key === '__mj_UpdatedAt') {
        return {
          ...col,
          format: (value: string) => this.utils.formatDate(value)
        };
      }
      if (col.key === 'engagement') {
        return {
          ...col,
          format: (_value: unknown, item: AnnouncementWithStats) => this.utils.formatEngagementRate(item)
        };
      }
      return col;
    });
  }

  private async loadAnnouncements(): Promise<void> {
    this.loading = true;
    try {
      const rv = new RunView();

      // Load announcements created by current user
      // Include deleted announcements only when specifically filtered for them
      let filter = `CreatedByUserID = '${this.currentUser.ID}'`;
      
      if (this.selectedStatus !== 'all') {
        filter += ` AND Status = '${this.selectedStatus}'`;
      } 

      const [announcementsResult, targetsResult, userStatesResult] = await rv.RunViews([
        {
          EntityName: 'Announcements',
          ExtraFilter: filter,
          OrderBy: '__mj_UpdatedAt DESC',
          ResultType: 'entity_object'
        },
        {
          EntityName: 'Announcement Targets',
          ExtraFilter: '',
          ResultType: 'entity_object'
        },
        {
          EntityName: 'Announcement User States',
          ExtraFilter: '',
          ResultType: 'entity_object'
        }
      ]);

      if (announcementsResult.Success) {
        const announcements = announcementsResult.Results as AnnouncementsEntity[];
        const targets = targetsResult.Success ? targetsResult.Results as AnnouncementTargetsEntity[] : [];
        const userStates = userStatesResult.Success ? userStatesResult.Results as AnnouncementUserStatesEntity[] : [];

        this.announcements = await Promise.all(announcements.map(async (announcement: AnnouncementsEntity) => {
          const stats = this.calculateAnnouncementStats(announcement.ID, targets, userStates);
          return {
            // Flatten announcement properties for grid
            ...announcement,
            announcement,
            ...stats,
            // Add computed properties for grid display
            Priority: announcement.Priority,
            Title: announcement.Title,
            Status: announcement.Status,
            engagement: stats
          };
        }));
        
        // Trigger change detection to update the grid
        this.cdr.detectChanges();
      }
    } catch (error) {
      LogError('Failed to load announcements:', undefined, error);
    } finally {
      this.loading = false;
    }
  }

  private calculateAnnouncementStats(announcementId: string, targets: AnnouncementTargetsEntity[], userStates: AnnouncementUserStatesEntity[]) {
    const announcementTargets = targets.filter(t => t.AnnouncementID === announcementId);
    const announcementStates = userStates.filter(s => s.AnnouncementID === announcementId);

    const totalTargets = announcementTargets.length;
    const readCount = announcementStates.filter(s => s.Status === 'read' || s.Status === 'acknowledged').length;
    const unreadCount = Math.max(0, totalTargets - announcementStates.length);
    const acknowledgedCount = announcementStates.filter(s => s.Status === 'acknowledged').length;
    const dismissedCount = announcementStates.filter(s => s.Status === 'dismissed').length;

    return {
      totalTargets,
      readCount,
      unreadCount,
      acknowledgedCount,
      dismissedCount
    };
  }

  get filteredAnnouncements(): AnnouncementWithStats[] {
    if (!this.searchText.trim()) {
      return this.announcements;
    }

    const search = this.searchText.toLowerCase().trim();
    return this.announcements.filter(item =>
      item.announcement.Title.toLowerCase().includes(search) ||
      item.announcement.Content.toLowerCase().includes(search)
    );
  }

  async onStatusFilterChange(): Promise<void> {
    await this.loadAnnouncements();
  }



  async deleteAnnouncement(announcementWithStats: AnnouncementWithStats): Promise<void> {
    if (confirm(`Are you sure you want to delete "${announcementWithStats.announcement.Title}"? This will mark the announcement as deleted.`)) {
      try {
        // Instead of hard delete, set Status to 'deleted'
        const announcement = announcementWithStats.announcement;
        announcement.Status = 'deleted';
        
        const result = await announcement.Save();
        if (result) {
          this.notificationService.success('Delete Successful', 'Announcement marked as deleted');
          await this.loadAnnouncements();
        }
      } catch (error) {
        LogError('Failed to delete announcement:', undefined, error);
        this.notificationService.error('Delete Failed', 'Failed to delete announcement');
      }
    }
  }

  async expireAnnouncement(announcementWithStats: AnnouncementWithStats): Promise<void> {
    try {
      const announcement = announcementWithStats.announcement;
      announcement.Status = 'expired';
      announcement.ExpiryDate = new Date();
      
      const result = await announcement.Save();
      if (result) {
        this.notificationService.success('Expire Successful', 'Announcement expired successfully');
        await this.loadAnnouncements();
      }
    } catch (error) {
      LogError('Failed to expire announcement:', undefined, error);
      this.notificationService.error('Expire Failed', 'Failed to expire announcement');
    }
  }



  showAnnouncementAnalytics(item: AnnouncementWithStats): void {
    this.selectedAnnouncement = item.announcement;
    this.showAnalyticsModal = true;
  }

  closeAnalyticsModal(): void {
    this.showAnalyticsModal = false;
    this.selectedAnnouncement = null;
  }

  viewAnnouncement(item: AnnouncementWithStats): void {
    this.selectedAnnouncement = item.announcement;
    this.showViewModal = true;
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.selectedAnnouncement = null;
  }


  formatDate(dateInput: string | Date): string {
    return this.utils.formatDate(dateInput.toString());
  }

  getPriorityIcon(priority: string): string {
    return this.utils.getPriorityIcon(priority);
  }

  getAnnouncementStats(announcementId: string): AnnouncementWithStats {
    const announcement = this.announcements.find(a => a.announcement.ID === announcementId);
    if (announcement) {
      return announcement;
    }
    return {
      announcement: this.selectedAnnouncement!,
      totalTargets: 0,
      readCount: 0,
      unreadCount: 0,
      acknowledgedCount: 0,
      dismissedCount: 0
    };
  }

  exportAnalytics(): void {
    if (!this.selectedAnnouncement) return;
    
    const stats = this.getAnnouncementStats(this.selectedAnnouncement.ID);
    const data = {
      announcement: {
        id: this.selectedAnnouncement.ID,
        title: this.selectedAnnouncement.Title,
        status: this.selectedAnnouncement.Status,
        priority: this.selectedAnnouncement.Priority,
        publishDate: this.selectedAnnouncement.PublishDate,
        expiryDate: this.selectedAnnouncement.ExpiryDate
      },
      statistics: {
        totalTargets: stats.totalTargets,
        readCount: stats.readCount,
        unreadCount: stats.unreadCount,
        acknowledgedCount: stats.acknowledgedCount,
        dismissedCount: stats.dismissedCount,
        engagementRate: this.getEngagementRate(stats)
      }
    };
    
    // Create and download JSON file
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `announcement-analytics-${this.selectedAnnouncement.ID}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    this.notificationService.success('Export Complete', 'Analytics data exported successfully');
  }

  getStatusClass(status: string): string {
    return `announcement-management__status--${status}`;
  }

  getStatusBadge(status: string): string {
    return this.utils.getStatusBadge(status);
  }

  getEngagementRate(item: AnnouncementWithStats): string {
    return this.utils.formatEngagementRate(item);
  }


  canDelete(announcement: AnnouncementsEntity): boolean {
    // Can delete if not already deleted and either:
    // - Not published yet
    // - Published but created by current user
    return announcement.Status !== 'deleted' && 
           announcement.CreatedByUserID === this.currentUser.ID;
  }

}